/**
 * MetricBarWidget — a set of registry metrics compared as bars, each drillable.
 *
 * Some KPI sets are a distribution, not a scoreboard: CAPA age buckets
 * (0-7 / 8-30 / 31-60 / 61-90 / >90 days) are read by comparing their lengths,
 * which five separate number cards make the reader do in their head. One bar
 * per metric shows the shape at a glance and still drills to the records.
 *
 * The bars are ordered as CONFIGURED, not by size: an age profile read out of
 * sequence says nothing. That is why this is not a /breakdown chart — a
 * breakdown sorts by count and groups one metric by a column, whereas this
 * charts several distinct metrics in a meaningful order.
 *
 * Unauthorized metrics are dropped (never shown as zero); a metric that errors
 * is left out with the others still drawn.
 */
import React, { useState } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { useKpis } from '../hooks/useDashboardData';
import ChartWidget from './ChartWidget';
import DrilldownModal from './DrilldownModal';

export default function MetricBarWidget({
  title,
  metrics = [],
  chartType = 'hbar',
  height,
  labels = {},
  showValues = true,
}) {
  const {
    module, siteId, time, departmentId, dimensions, fyStartMonth, standard, refreshMs,
  } = useDashboard();
  const [drill, setDrill] = useState(null);

  const { metrics: values, loading, error, unauthorized } = useKpis({
    module, metrics, siteId, time, departmentId, dimensions, fyStartMonth, standard, refreshMs,
  });

  const unauth = new Set(unauthorized || []);
  const rows = metrics
    .filter((key) => !unauth.has(key))
    .map((key) => {
      const m = values[key];
      if (!m || m.error) return null;
      return {
        key,
        label: labels[key] || m.label || key,
        value: m.value ?? 0,
        drillable: m.drillable !== false,
      };
    })
    .filter(Boolean);

  const allZero = rows.length > 0 && rows.every((r) => !r.value);

  return (
    <>
      <ChartWidget
        type={chartType}
        // An all-zero distribution is a real answer ("nothing is in any bucket"),
        // but drawing five empty bars reads as broken — hand it to the empty state.
        data={allZero ? [] : rows}
        xKey="label"
        series={[{ key: 'value', label: 'Records' }]}
        title={title}
        height={height || Math.max(200, rows.length * 34 + 40)}
        showValues={showValues}
        loading={loading}
        error={error}
        onPointClick={(d) => {
          const row = d?.payload || d;
          if (row?.key && row.drillable) setDrill(row);
        }}
      />
      <DrilldownModal
        open={!!drill}
        metric={drill?.key}
        title={drill?.label}
        subtitle="Click a row to open the record."
        onClose={() => setDrill(null)}
      />
    </>
  );
}
